import { useState, useEffect, useRef, useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

const POSITIONS = [
  { id: 'tl', label: 'Top-left' },
  { id: 'tc', label: 'Top-center' },
  { id: 'tr', label: 'Top-right' },
  { id: 'cl', label: 'Center-left' },
  { id: 'cc', label: 'Center' },
  { id: 'cr', label: 'Center-right' },
  { id: 'bl', label: 'Bottom-left' },
  { id: 'bc', label: 'Bottom-center' },
  { id: 'br', label: 'Bottom-right' },
];

interface WatermarkOpts {
  text: string;
  size: number;
  opacity: number;
  color: string;
  pos: string;
  tile: boolean;
}

/** Shared draw routine — used by both live preview and bulk export. */
function drawWatermark(canvas: HTMLCanvasElement, img: HTMLImageElement, o: WatermarkOpts): void {
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.drawImage(img, 0, 0);
  ctx.font = `600 ${o.size}px Inter, sans-serif`;
  ctx.fillStyle = o.color;
  ctx.globalAlpha = o.opacity / 100;

  if (o.tile) {
    ctx.save();
    const m = ctx.measureText(o.text);
    const stepX = m.width + o.size;
    const stepY = o.size * 2.5;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 6);
    for (let y = -canvas.height; y < canvas.height; y += stepY) {
      for (let x = -canvas.width; x < canvas.width; x += stepX) {
        ctx.fillText(o.text, x, y);
      }
    }
    ctx.restore();
  } else {
    const m = ctx.measureText(o.text);
    const padding = o.size * 0.5;
    let x = padding;
    let y = padding + o.size;
    if (o.pos.includes('c') && o.pos.startsWith('c')) y = canvas.height / 2;
    if (o.pos.startsWith('b')) y = canvas.height - padding;
    if (o.pos.includes('c') && o.pos[1] === 'c') x = (canvas.width - m.width) / 2;
    if (o.pos.endsWith('r')) x = canvas.width - m.width - padding;
    if (o.pos[1] === 'l') x = padding;
    ctx.fillText(o.text, x, y);
  }
  ctx.globalAlpha = 1;
}

export function ImageWatermark() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [previewImg, setPreviewImg] = useState<HTMLImageElement | null>(null);
  const [text, setText] = useState('© Utility OS');
  const [size, setSize] = useState(48);
  const [opacity, setOpacity] = useState(60);
  const [color, setColor] = useState('#ffffff');
  const [pos, setPos] = useState('br');
  const [tile, setTile] = useState(false);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleFiles = useCallback(async (incoming: File[]) => {
    if (!incoming.length) return;
    setFiles((prev) => {
      const combined = [...prev, ...incoming.filter((f) => f.type.startsWith('image/'))];
      // Load preview from new first file if we don't have one yet.
      if (!prev.length && combined[0]) {
        readFileAsDataURL(combined[0])
          .then(loadImage)
          .then(setPreviewImg);
      }
      return combined;
    });
  }, []);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setPreviewImg(null);
    } else if (i === 0) {
      readFileAsDataURL(next[0]).then(loadImage).then(setPreviewImg);
    }
  }

  // Live preview reflects FIRST image
  useEffect(() => {
    if (!previewImg || !canvasRef.current) return;
    drawWatermark(canvasRef.current, previewImg, { text, size, opacity, color, pos, tile });
  }, [previewImg, text, size, opacity, color, pos, tile]);

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      const opts: WatermarkOpts = { text, size, opacity, color, pos, tile };
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const im = await loadImage(url);
          const c = document.createElement('canvas');
          drawWatermark(c, im, opts);
          const blob = await canvasToBlob(c, 'image/png');
          return { blob, filename: `${stripExt(file.name)}-watermark.png` };
        },
        {
          zipName: 'watermarked',
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-watermark"
        title="Image watermark"
        description="Stamp text watermarks with custom color, opacity, and position. Drop multiple files for bulk."
        badges={files.length > 1 ? <Badge variant="accent">{files.length} files</Badge> : null}
      />

      {files.length === 0 ? (
        <Dropzone accept="image/*" multiple onFiles={handleFiles} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <Field label="Watermark text">
              <Input value={text} onChange={(e) => setText(e.target.value)} />
            </Field>
            <Field label="Color">
              <div className="flex items-center gap-2">
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="!w-12 !h-9" />
                <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono" />
              </div>
            </Field>
            <Field label="Size" hint={`${size}px`}>
              <Slider value={size} onChange={setSize} min={12} max={200} step={2} showValue={false} />
            </Field>
            <Field label="Opacity" hint={`${opacity}%`}>
              <Slider value={opacity} onChange={setOpacity} min={5} max={100} showValue={false} />
            </Field>
            <Field label="Position">
              <Select
                value={pos}
                onChange={(e) => setPos(e.target.value)}
                options={POSITIONS.map((p) => ({ value: p.id, label: p.label }))}
                disabled={tile}
              />
            </Field>
            <label className="flex items-center justify-between cursor-pointer text-sm">
              <span className="text-fg-muted">Tile pattern</span>
              <input type="checkbox" checked={tile} onChange={(e) => setTile(e.target.checked)} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFiles([]);
                  setPreviewImg(null);
                }}
              >
                Clear
              </Button>
              <Button variant="primary" size="sm" loading={working} onClick={exportAll}>
                <Download size={11} />
                {files.length > 1 ? `Export ${files.length}` : 'Export'}
              </Button>
            </div>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview {files.length > 1 && <span className="text-fg-subtle">· first of {files.length}</span>}
            </div>
            <div className="p-6 bg-bg-inset min-h-[360px] flex items-center justify-center">
              <canvas ref={canvasRef} className="max-w-full max-h-[420px] rounded shadow-card" />
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default ImageWatermark;
